import * as React from 'react';
import * as Validator from '../../utils/validator';
import isEqual = require('lodash/isEqual');
import keys = require('lodash/keys');
import find = require('lodash/find');

/**
 * @see Use Formik instead of this HOC
 */

export interface IFormComponentProps {
  fields: Array<IFormField>;
  isFormValid: () => boolean;
  fieldNameWithFirstError: string;
  componentChangeField?: () => void;
  getDirtyFields: () => IFormDictionary;
  getFieldValue: (fieldName: string) => any;
  onSaveForm: (saveComponentForm: () => void) => void;
  setError: (fieldName: string, error: string) => void;
  onChangeFormField: (fieldName: string, value: any) => void;
  forceUpdateFields: () => void;
}

export interface IFormDictionary {
  [name: string]: any;
}

interface IFormSchema {
  fieldName: string;
  value: any;
}

export interface IFormField extends IFormSchema {
  validate: (value: any) => boolean;
  isDirty: boolean;
  error: any;
}

interface IFormState {
  fieldNameWithFirstError?: string | undefined;
  isFormSubmitted?: boolean;
  fields?: IFormField[];
  formData?: any;
  apiError: any;
}

const buildFormFields = (formData: IFormSchema[], apiError: string): IFormField[] => {
  if (!formData) {
    return [];
  }

  return keys(formData).map((key: string): IFormField => {
    const fieldName = key;
    const value = formData[key];
    const validate = Validator.getValidator(fieldName, value);

    return {fieldName, value, validate, error: '', isDirty: !!apiError};
  });
};

export const Form = (Component) => {
  return class extends React.Component<any, IFormState> {
    public static getDerivedStateFromProps({formData, apiError}: any, state: IFormState): IFormState {
      if (!isEqual(formData, state.formData) || apiError !== state.apiError) {
        return {formData, apiError, fields: buildFormFields(formData, apiError)};
      }

      return null;
    }

    public constructor(props: any) {
      super(props);

      this.state = {
        fieldNameWithFirstError: undefined,
        isFormSubmitted: false,
        formData: props.formData,
        apiError: props.apiError,
        fields: buildFormFields(props.formData, props.apiError),
      };
    }

    public render(): React.ReactElement<any> {
      return (
        <Component
          {...this.state}
          {...this.props}
          setError={this.setError}
          onSaveForm={this.onSaveForm}
          isFormValid={this.isFormValid}
          getFieldValue={this.getFieldValue}
          getDirtyFields={this.getDirtyFields}
          onChangeFormField={this.onChangeFormField}
          forceUpdateFields={this.forceUpdateFields}
        />
      );
    }

    public forceUpdateFields = () => (
      this.setState({fields: buildFormFields(this.props.formData, this.props.apiError)})
    )

    public onChangeFormField = (fieldName: string, value: any): void => {
      const fields = this.state.fields.map((field: IFormField) =>
        field.fieldName === fieldName
          ? {...field, value, error: '', isDirty: true}
          : field,
      );

      this.setState({
        fields,
        fieldNameWithFirstError: '',
        isFormSubmitted: false,
      });

      const componentChangeField = (this.props as any).componentChangeField;
      if (componentChangeField) {
        componentChangeField();
      }
    };

    public onSaveForm = (saveComponentForm: () => void): void => {
      this.setState({
        isFormSubmitted: true,
      });

      if (this.isFormValid()) {
        saveComponentForm();
      }
    };

    public isFormValid = (): boolean => {
      const fields = this.state.fields.map((field: IFormField) => (
        {...field, error: field.validate ? field.validate(field.value) : ''}
      ));
      const fieldNameWithFirstError = this.getFieldNameWithFirstError(fields);

      this.setState({
        fields,
        fieldNameWithFirstError,
      });
      return !fieldNameWithFirstError;
    };

    public setError = (fieldName: string, error: string): void => {
      const fields = this.state.fields.map((field: IFormField) =>
        field.fieldName === fieldName
        ? {...field, error}
        : {...field, error: field.validate ? field.validate(field.value) : ''},
      );
      this.setState({
        fields,
      });
    };

    public getFieldValue = (fieldName: string): any => {
      const formField = this.state.fields
        .find(({fieldName: name}) => fieldName === name);
      return formField && formField.value;
    };

    public getDirtyFields = (): IFormDictionary => {
      return this.state.fields
        .filter(({isDirty}) => isDirty)
        .reduce((fields: any, {fieldName, value}: IFormField) => ({...fields, [fieldName]: value}), {});
    };

    private getFieldNameWithFirstError(fields: Array<IFormField>): string {
      const fieldWithFirstError = find(fields, (field: IFormField) => !!field.error);
      return fieldWithFirstError ? fieldWithFirstError.fieldName : '';
    }
  };
};
